/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/naming-convention */
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next/typescript/t';
import { AddressDataValues } from '../../../models/addressDataValues';
import { AddressItemContainer } from './styles';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/delete-icon.svg';
import Radio from '../../../common/radio/radio.component';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../common/tooltip/tooltip.component';
import Spinner from '../../../common/spinner/spinner.component';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';

function getAddressDescription(
  t: TFunction<'translation', undefined>,
  address: AddressDataValues
) {
  const res: string[] = [];
  const addressComponents: {
    fieldName: keyof AddressDataValues;
    label?: string;
  }[] = [
    { fieldName: 'street' },
    { fieldName: 'building' },
    { fieldName: 'apartment', label: 'apartment' },
    { fieldName: 'section', label: 'section' },
    { fieldName: 'buzz_code', label: 'buzz_code' },
    { fieldName: 'city' },
  ];

  addressComponents.forEach((x) => {
    const addressComponent = address[x.fieldName];
    if (!addressComponent) return;

    res.push(
      x.label ? `${t(x.label)} ${addressComponent}` : `${addressComponent}`
    );
  });

  return res.join(', ');
}

interface Props {
  address: AddressDataValues;
  isDisabled?: boolean;
  isLoading?: boolean;
  onSelect?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const AddressItem = ({
  address,
  isDisabled,
  isLoading,
  onSelect,
  onEdit,
  onDelete,
}: Props) => {
  const { t } = useTranslation();

  const isMobile = useIsMobile();

  const description = getAddressDescription(t, address);

  const handleSelect = () => {
    if (isDisabled || isLoading) return;

    onSelect?.();
  };

  const controls = (
    <div className='controls'>
      <button
        className='buttonControlIcon edit'
        disabled={isDisabled}
        onClick={(e) => {
          e.stopPropagation();
          onEdit?.();
        }}
        type='button'>
        <EditIcon />
      </button>
      <Tooltip placement='bottom'>
        <TooltipTrigger asChild>
          <button
            className='buttonControlIcon delete'
            disabled={address?.is_default || isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
            type='button'>
            <DeleteIcon />
          </button>
          {address?.is_default && (
            <TooltipContent maxWidth='25rem'>
              {t('delete_address_hint')}
            </TooltipContent>
          )}
        </TooltipTrigger>
      </Tooltip>
    </div>
  );

  return (
    <AddressItemContainer
      $isLoading={isLoading}
      disabled={isDisabled}
      $isSelected={address.is_default}
      onClick={(e) => {
        e.stopPropagation();
        handleSelect();
      }}>
      {isLoading && (
        <div className='spinnerOverlay'>
          <Spinner isSmall />
        </div>
      )}
      <div className='topPart'>
        <p className='title'>{address.title}</p>
        <div className='topRightPart'>
          {address?.is_default && (
            <span className='defaultPill'>{t('default_address_label')}</span>
          )}
          {!isMobile && controls}
          <Radio
            checked={!!address.is_default}
            disabled={isDisabled || isLoading}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={handleSelect}
          />
        </div>
      </div>
      <div className='bottomPart'>
        <p className='description'>{description}</p>
        {isMobile && controls}
      </div>
    </AddressItemContainer>
  );
};

export default AddressItem;
